/**
 * Error Handler Utility
 * Provides comprehensive error handling for API requests including:
 * - Network connectivity issues
 * - Request timeouts
 * - Server errors
 * - Authentication errors
 * - Rate limiting
 */

import { toast } from 'react-toastify';

// Error types for categorization
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  SERVER: 'SERVER_ERROR',
  AUTH: 'AUTH_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  RATE_LIMIT: 'RATE_LIMIT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// User-friendly error messages
export const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: {
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection and try again.',
    action: 'Check your internet connection'
  },
  [ERROR_TYPES.TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The server is taking too long to respond. Please try again.',
    action: 'Wait a moment and retry'
  },
  [ERROR_TYPES.SERVER]: {
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    action: 'Try again in a few minutes'
  },
  [ERROR_TYPES.AUTH]: {
    title: 'Authentication Error',
    message: 'Your session has expired or you are not authorized. Please login again.',
    action: 'Login again'
  },
  [ERROR_TYPES.VALIDATION]: {
    title: 'Validation Error',
    message: 'Please check your input and try again.',
    action: 'Review your input'
  },
  [ERROR_TYPES.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource was not found.',
    action: 'Go back and try again'
  },
  [ERROR_TYPES.RATE_LIMIT]: {
    title: 'Too Many Requests',
    message: 'You are making too many requests. Please slow down.',
    action: 'Wait a moment before trying again'
  },
  [ERROR_TYPES.UNKNOWN]: {
    title: 'Error',
    message: 'An unexpected error occurred. Please try again.',
    action: 'Try again'
  }
};

/**
 * Check if the browser is currently online
 * @returns {boolean}
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Categorize an error into a specific type
 * @param {Error|Object} error - The error object
 * @returns {string} - The error type
 */
export const categorizeError = (error) => {
  // Check for network errors first
  if (!isOnline()) {
    return ERROR_TYPES.NETWORK;
  }

  // Axios-specific error handling
  if (error.code) {
    switch (error.code) {
      case 'ECONNABORTED':
        return ERROR_TYPES.TIMEOUT;
      case 'ERR_NETWORK':
      case 'ERR_CONNECTION_REFUSED':
      case 'ERR_CONNECTION_RESET':
      case 'ERR_INTERNET_DISCONNECTED':
        return ERROR_TYPES.NETWORK;
      case 'ERR_CANCELED':
        return ERROR_TYPES.TIMEOUT;
      default:
        break;
    }
  }

  // Check for timeout based on message
  if (error.message) {
    const message = error.message.toLowerCase();
    if (message.includes('timeout') || message.includes('timed out')) {
      return ERROR_TYPES.TIMEOUT;
    }
    if (message.includes('network') || message.includes('connection') || message.includes('offline')) {
      return ERROR_TYPES.NETWORK;
    }
  }

  // HTTP status code based categorization
  if (error.response?.status) {
    const status = error.response.status;
    
    if (status === 401 || status === 403) {
      return ERROR_TYPES.AUTH;
    }
    if (status === 404) {
      return ERROR_TYPES.NOT_FOUND;
    }
    if (status === 422 || status === 400) {
      return ERROR_TYPES.VALIDATION;
    }
    if (status === 429) {
      return ERROR_TYPES.RATE_LIMIT;
    }
    if (status >= 500) {
      return ERROR_TYPES.SERVER;
    }
  }

  // Check if it's a TypeError (often indicates network issues)
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return ERROR_TYPES.NETWORK;
  }

  return ERROR_TYPES.UNKNOWN;
};

/**
 * Get a user-friendly error message
 * @param {Error|Object} error - The error object
 * @returns {Object} - Error details with title, message, and action
 */
export const getErrorDetails = (error) => {
  const errorType = categorizeError(error);
  const defaultDetails = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN];
  
  // Try to extract server-provided message
  const serverMessage = error.response?.data?.error || 
                       error.response?.data?.message || 
                       error.error ||
                       error.message;

  return {
    type: errorType,
    title: defaultDetails.title,
    message: serverMessage || defaultDetails.message,
    action: defaultDetails.action,
    originalError: error,
    status: error.response?.status
  };
};

/**
 * Display error toast with appropriate styling
 * @param {Error|Object} error - The error object
 * @param {Object} options - Additional options
 */
export const showErrorToast = (error, options = {}) => {
  const details = getErrorDetails(error);
  const { 
    showRetry = false, 
    onRetry = null,
    customMessage = null,
    autoClose = 5000
  } = options;

  // Construct the message
  let toastMessage = customMessage || `${details.title}\n${details.message}`;
  
  // Add retry hint for recoverable errors
  if (showRetry && (details.type === ERROR_TYPES.NETWORK || details.type === ERROR_TYPES.TIMEOUT)) {
    toastMessage += '\n\nClick to retry or wait and try again.';
  }

  // Configure toast based on error type
  const toastConfig = {
    autoClose: details.type === ERROR_TYPES.NETWORK ? false : autoClose, // Keep network errors visible
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    onClick: onRetry
  };

  // Use different toast types based on error severity
  if (details.type === ERROR_TYPES.NETWORK || details.type === ERROR_TYPES.TIMEOUT) {
    toast.error(toastMessage, {
      ...toastConfig,
      icon: details.type === ERROR_TYPES.NETWORK ? '' : '',
      style: {
        background: '#F9EEEB',
        color: '#5F241B',
        border: '1px solid #E5B3A9'
      }
    });
  } else if (details.type === ERROR_TYPES.AUTH) {
    toast.warning(toastMessage, {
      ...toastConfig,
      icon: false
    });
  } else {
    toast.error(toastMessage, toastConfig);
  }

  return details;
};

/**
 * Handle API errors with comprehensive error handling
 * @param {Error|Object} error - The error object
 * @param {Object} options - Configuration options
 * @returns {Object} - Error details for further handling
 */
export const handleApiError = (error, options = {}) => {
  const {
    showToast = true,
    logError = true,
    context = 'API Request',
    onNetworkError = null,
    onTimeoutError = null,
    onAuthError = null,
    onServerError = null
  } = options;

  const details = getErrorDetails(error);

  // Log error for debugging
  if (logError) {
    console.error(`${context} Error:`, {
      type: details.type,
      title: details.title,
      message: details.message,
      status: details.status,
      originalError: error
    });
  }

  // Execute type-specific callbacks
  switch (details.type) {
    case ERROR_TYPES.NETWORK:
      if (onNetworkError) onNetworkError(details);
      break;
    case ERROR_TYPES.TIMEOUT:
      if (onTimeoutError) onTimeoutError(details);
      break;
    case ERROR_TYPES.AUTH:
      if (onAuthError) onAuthError(details);
      // Optionally redirect to login
      break;
    case ERROR_TYPES.SERVER:
      if (onServerError) onServerError(details);
      break;
    default:
      break;
  }

  // Show toast notification
  if (showToast) {
    showErrorToast(error, options);
  }

  return details;
};

/**
 * Create an error handler with default options
 * @param {Object} defaultOptions - Default options for error handling
 * @returns {Function} - Error handler function
 */
export const createErrorHandler = (defaultOptions = {}) => {
  return (error, overrideOptions = {}) => {
    return handleApiError(error, { ...defaultOptions, ...overrideOptions });
  };
};

/**
 * Retry wrapper for API calls with exponential backoff
 * @param {Function} apiCall - The API call function
 * @param {Object} options - Retry options
 * @returns {Promise} - The API response
 */
export const withRetry = async (apiCall, options = {}) => {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    retryOn = [ERROR_TYPES.NETWORK, ERROR_TYPES.TIMEOUT, ERROR_TYPES.SERVER],
    onRetry = null,
    showProgress = true
  } = options;

  let lastError;
  let delay = initialDelay;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      const result = await apiCall();
      return result;
    } catch (error) {
      lastError = error;
      const errorType = categorizeError(error);

      // Check if we should retry
      if (attempt <= maxRetries && retryOn.includes(errorType)) {
        if (showProgress) {
          toast.info(`Request failed. Retrying... (${attempt}/${maxRetries})`, {
            autoClose: delay - 500,
            icon: false
          });
        }

        if (onRetry) {
          onRetry(attempt, errorType);
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Increase delay with exponential backoff
        delay = Math.min(delay * backoffFactor, maxDelay);
      } else {
        throw error;
      }
    }
  }

  throw lastError;
};

/**
 * Network status monitor
 * Automatically shows/hides offline notification
 */
class NetworkMonitor {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = [];
    this.offlineToastId = null;
    this.init();
  }

  init() {
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  handleOnline() {
    this.isOnline = true;
    
    // Dismiss offline toast
    if (this.offlineToastId) {
      toast.dismiss(this.offlineToastId);
      this.offlineToastId = null;
    }
    
    // Show online notification
    toast.success('Back Online! Connection restored.', {
      autoClose: 3000,
      icon: false
    });

    // Notify listeners
    this.listeners.forEach(listener => listener(true));
  }

  handleOffline() {
    this.isOnline = false;
    
    // Show persistent offline notification
    this.offlineToastId = toast.error(
      'You are offline. Please check your internet connection.',
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        icon: false,
        style: {
          background: '#F9EEEB',
          color: '#5F241B',
          border: '2px solid #8E3628'
        }
      }
    );

    // Notify listeners
    this.listeners.forEach(listener => listener(false));
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  getStatus() {
    return this.isOnline;
  }

  destroy() {
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
  }
}

// Create singleton instance
export const networkMonitor = new NetworkMonitor();

/**
 * React hook for network status
 * Usage: const isOnline = useNetworkStatus();
 */
export const useNetworkStatusInit = () => {
  // This is a placeholder - the actual React hook should be in a separate hooks file
  // to avoid importing React in this utility file
  return networkMonitor.getStatus();
};

/**
 * Check network before making API call
 * @param {Function} apiCall - The API call function
 * @returns {Promise} - The API response or throws network error
 */
export const withNetworkCheck = async (apiCall) => {
  if (!isOnline()) {
    const error = new Error('No internet connection');
    error.code = 'ERR_NETWORK';
    error.isNetworkError = true;
    throw error;
  }
  return apiCall();
};

/**
 * Format error for display in UI components
 * @param {Error|Object} error - The error object
 * @returns {Object} - Formatted error for UI
 */
export const formatErrorForUI = (error) => {
  const details = getErrorDetails(error);
  
  return {
    icon: getErrorIcon(details.type),
    title: details.title,
    message: details.message,
    action: details.action,
    canRetry: [ERROR_TYPES.NETWORK, ERROR_TYPES.TIMEOUT, ERROR_TYPES.SERVER].includes(details.type),
    isAuthError: details.type === ERROR_TYPES.AUTH
  };
};

/**
 * Get appropriate icon for error type
 * @param {string} errorType - The error type
 * @returns {string} - Emoji icon
 */
export const getErrorIcon = (errorType) => {
  const icons = {
    [ERROR_TYPES.NETWORK]: '',
    [ERROR_TYPES.TIMEOUT]: '',
    [ERROR_TYPES.SERVER]: '',
    [ERROR_TYPES.AUTH]: '',
    [ERROR_TYPES.VALIDATION]: '',
    [ERROR_TYPES.NOT_FOUND]: '',
    [ERROR_TYPES.RATE_LIMIT]: '',
    [ERROR_TYPES.UNKNOWN]: ''
  };
  return icons[errorType] || '';
};

// Alias for backward compatibility
export const showApiError = showErrorToast;

export default {
  ERROR_TYPES,
  ERROR_MESSAGES,
  isOnline,
  categorizeError,
  getErrorDetails,
  showErrorToast,
  showApiError,
  handleApiError,
  createErrorHandler,
  withRetry,
  networkMonitor,
  withNetworkCheck,
  formatErrorForUI,
  getErrorIcon
};
